import {
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    MoneyMapRuleGoalType,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { useAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    BodyLargeRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    DollarInput,
    IconSelectDropdown,
    Input,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    PercentInput,
    Sheet,
    XStack,
    YStack,
} from 'smoothie';

import { MONEY_MAP_NAME_MAX_LENGTH } from '../constants';
import {
    addRuleInMoneyMap,
    editableMoneyMapAtom,
} from '../state/editableMoneyMapAtom';
import {
    isMoneyMapAccountOwnedByUser,
    sortMoneyMapAccounts,
} from '../utils/moneyMapAccountUtils';
import {
    parseDollarInputToCents,
    parsePercentInputToDecimal,
} from '../utils/moneyMapFormInputUtils';
import { isTargetBalanceEligibleAccountType } from '../utils/moneyMapRuleUtils';
import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';

interface MoneyMapAddGoalSheetProps {
    isOpen: boolean;
    onClose: () => void;
    ruleGroupId: string | null;
    accounts: MoneyMapAccountWithAccountDetails[];
    currentUserId: string;
}

export const MoneyMapAddGoalSheet = ({
    isOpen,
    onClose,
    ruleGroupId,
    accounts,
    currentUserId,
}: MoneyMapAddGoalSheetProps) => {
    const [editableMoneyMap, setEditableMoneyMap] =
        useAtom(editableMoneyMapAtom);

    const [goalName, setGoalName] = useState('');
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
        null,
    );
    const [percentage, setPercentage] = useState('');
    const [targetBalance, setTargetBalance] = useState('');
    const [monthlyCap, setMonthlyCap] = useState('');
    const [yearlyCap, setYearlyCap] = useState('');

    // Quiltt-linked accounts (external liabilities/accounts synced via
    // Quiltt) aren't valid goal destinations for members. `quilttAccountId`
    // is the same field Grove's AccountSettingsFormModule.tsx checks to
    // exclude Quiltt accounts from its transfer-account picker. Accounts
    // owned solely by a partner on a shared membership aren't selectable
    // either — a member can only route a new goal to their own or a joint
    // account, never one that belongs only to someone else.
    const selectableAccounts = useMemo(
        () =>
            sortMoneyMapAccounts(
                accounts.filter(
                    (account) =>
                        account.quilttAccountId == null &&
                        isMoneyMapAccountOwnedByUser(account, currentUserId),
                ),
                editableMoneyMap,
            ),
        [accounts, currentUserId, editableMoneyMap],
    );

    useEffect(() => {
        if (isOpen) {
            setGoalName('');
            setSelectedAccountId(null);
            setPercentage('');
            setTargetBalance('');
            setMonthlyCap('');
            setYearlyCap('');
        }
    }, [isOpen]);

    const handleClose = onClose;

    const accountOptions = useMemo(
        () =>
            selectableAccounts.map((account) => ({
                value: account.id,
                label: account.accountDetails.name,
                iconElement: (
                    <MoneyMapAccountIcon
                        category={MoneyMapCategory.GOALS}
                        account={account}
                    />
                ),
            })),
        [selectableAccounts],
    );

    const selectedAccount = useMemo(
        () =>
            selectableAccounts.find((a) => a.id === selectedAccountId) ?? null,
        [selectableAccounts, selectedAccountId],
    );

    // Grove locks external and investment-management accounts to
    // "contribute indefinitely" (no target balance) — keep members from
    // setting up a goal Grove itself wouldn't allow.
    const targetBalanceEligible =
        selectedAccount == null ||
        isTargetBalanceEligibleAccountType(selectedAccount.type);

    const canSave = useMemo(() => {
        if (goalName.trim().length === 0) return false;
        if (selectedAccountId == null) return false;

        const pct = parsePercentInputToDecimal(percentage);
        if (pct == null || pct <= 0 || pct > 1) return false;

        const dollarInputsToCheck = targetBalanceEligible
            ? [targetBalance, monthlyCap, yearlyCap]
            : [monthlyCap, yearlyCap];
        for (const optionalDollarInput of dollarInputsToCheck) {
            const cents = parseDollarInputToCents(optionalDollarInput);
            if (cents != null && cents <= 0) return false;
        }

        return true;
    }, [
        goalName,
        selectedAccountId,
        percentage,
        targetBalance,
        targetBalanceEligible,
        monthlyCap,
        yearlyCap,
    ]);

    const handleSave = () => {
        if (
            editableMoneyMap == null ||
            ruleGroupId == null ||
            selectedAccount == null
        ) {
            return;
        }

        const targetBalanceCents = targetBalanceEligible
            ? parseDollarInputToCents(targetBalance)
            : null;

        const groupRules = editableMoneyMap.moneyMapRules.filter(
            (r) => r.moneyMapRuleGroupId === ruleGroupId,
        );
        const priority =
            groupRules.reduce((max, r) => Math.max(max, r.priority), 0) + 1;

        const newRule: MoneyMapRuleWithAccount = {
            id: `new-goal-${Date.now()}`,
            moneyMapId: editableMoneyMap.id,
            moneyMapAccountId: selectedAccount.id,
            moneyMapAccount: selectedAccount,
            moneyMapRuleGroupId: ruleGroupId,
            type: MoneyMapRuleType.GOAL,
            goalType:
                targetBalanceCents != null
                    ? MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE
                    : MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
            description: goalName.trim(),
            priority,
            routingRuleType:
                targetBalanceCents != null
                    ? PaycheckRoutingRuleType.MINIMUM_BALANCE
                    : PaycheckRoutingRuleType.PERCENTAGE,
            percentage: parsePercentInputToDecimal(percentage),
            fixedAmountCents: null,
            targetBalanceCents,
            monthlyContributionMaxCents: parseDollarInputToCents(monthlyCap),
            yearlyContributionMaxCents: parseDollarInputToCents(yearlyCap),
            createdAt: new Date().toISOString(),
            updatedAt: null,
        };

        setEditableMoneyMap(addRuleInMoneyMap(editableMoneyMap, newRule));
        handleClose();
    };

    return (
        <Sheet isOpen={isOpen} onClose={handleClose} showCloseButton={false}>
            <YStack $sm={{ minWidth: 400 }} gap={24}>
                <BodyLargeRegular textAlign="center">
                    Create new goal
                </BodyLargeRegular>

                <YStack gap={8}>
                    <LabelRegular marginLeft={8} color="$gray1100">
                        Goal name
                    </LabelRegular>
                    <KeyboardCompatibleBottomSheetTextInput
                        component={Input}
                        value={goalName}
                        onChangeText={setGoalName}
                        maxLength={MONEY_MAP_NAME_MAX_LENGTH}
                        placeholder="Enter goal name"
                        returnKeyType="done"
                        onSubmitEditing={() => {
                            Keyboard.dismiss();
                        }}
                        fontWeight="regular"
                    />
                </YStack>

                <YStack gap={8}>
                    <LabelRegular marginLeft={8} color="$gray1100">
                        Destination account
                    </LabelRegular>
                    <IconSelectDropdown
                        variant="field"
                        placeholder="Select account"
                        value={selectedAccountId ?? undefined}
                        onValueChange={setSelectedAccountId}
                        options={accountOptions}
                    />
                </YStack>

                <YStack gap={8}>
                    <LabelRegular marginLeft={8} color="$gray1100">
                        Percentage
                    </LabelRegular>
                    <KeyboardCompatibleBottomSheetTextInput
                        component={PercentInput}
                        value={percentage}
                        onChangeText={setPercentage}
                        placeholder="Enter percentage allocation"
                        returnKeyType="done"
                        onSubmitEditing={() => {
                            Keyboard.dismiss();
                        }}
                        fontWeight="regular"
                    />
                </YStack>

                {targetBalanceEligible && (
                    <YStack gap={8}>
                        <LabelRegular marginLeft={8} color="$gray1100">
                            Target balance (optional)
                        </LabelRegular>
                        <KeyboardCompatibleBottomSheetTextInput
                            component={DollarInput}
                            value={targetBalance}
                            onChangeText={setTargetBalance}
                            allowDecimals={false}
                            returnKeyType="done"
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                            fontWeight="regular"
                        />
                    </YStack>
                )}

                <XStack gap={12}>
                    <YStack gap={8} flex={1}>
                        <LabelRegular marginLeft={8} color="$gray1100">
                            Monthly limit (optional)
                        </LabelRegular>
                        <KeyboardCompatibleBottomSheetTextInput
                            component={DollarInput}
                            value={monthlyCap}
                            onChangeText={setMonthlyCap}
                            allowDecimals={false}
                            returnKeyType="done"
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                            fontWeight="regular"
                        />
                    </YStack>
                    <YStack gap={8} flex={1}>
                        <LabelRegular marginLeft={8} color="$gray1100">
                            Annual limit (optional)
                        </LabelRegular>
                        <KeyboardCompatibleBottomSheetTextInput
                            component={DollarInput}
                            value={yearlyCap}
                            onChangeText={setYearlyCap}
                            allowDecimals={false}
                            returnKeyType="done"
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                            fontWeight="regular"
                        />
                    </YStack>
                </XStack>

                <XStack gap={12}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={handleClose}
                        buttonProps={{ flexShrink: 1 }}
                    />
                    <Button
                        disabled={!canSave}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Create goal"
                        onPress={handleSave}
                        buttonProps={{ flexShrink: 1 }}
                    />
                </XStack>
            </YStack>
        </Sheet>
    );
};
